import {
    ButtonItem,
    JsonItem,
    ProgressBarItem,
    type ProgressTone,
    BarItem,
    type BarTone,
    HouseGridItem,
    type HouseGridEntry,
    PeopleListItem,
    type PeopleListEntry,
    type TabScrollState,
    SectionItem,
    SpacerItem,
    DividerItem,
    TabBarItem,
    TextItem,
    type TabItem,
    type UiItem,
    LinearLayoutStyle,
    LinearLayoutItem,
    GridLayoutStyle,
    GridLayoutItem,
    WrappedTextOptions,
    WrappedTextItem,
} from './items';
import type { TextVariant } from './ModalTheme';

export class ContentBuilder {
    private items: UiItem[] = [];

    getItems(): UiItem[] {
        return this.items;
    }

    text(text: string, variant: TextVariant = 'body'): void {
        this.items.push(new TextItem(text, variant));
    }

    spacer(height: number): void {
        this.items.push(new SpacerItem(height));
    }

    divider(height: number, color: string): void {
        this.items.push(new DividerItem(height, color));
    }

    button(id: string, label: string, enabled: boolean): void {
        this.items.push(new ButtonItem(id, label, enabled));
    }

    progress(label: string, value: number, max: number, tone?: ProgressTone): void {
        this.items.push(new ProgressBarItem(label, value, max, tone));
    }

    bar(label: string, value: number, max: number, tone: BarTone): void {
        this.items.push(new BarItem(label, value, max, tone));
    }

    houseGrid(items: HouseGridEntry[]): void {
        this.items.push(new HouseGridItem(items));
    }

    peopleList(items: PeopleListEntry[]): void {
        this.items.push(new PeopleListItem(items));
    }

    tabs(tabs: TabItem[], scrollState?: TabScrollState): void {
        this.items.push(new TabBarItem(tabs, scrollState));
    }

    jsonBlock(obj: unknown): void {
        this.items.push(new JsonItem(obj));
    }

    section(title: string, build: (builder: ContentBuilder) => void): void {
        const childBuilder = new ContentBuilder();
        build(childBuilder);
        this.items.push(new SectionItem(title, childBuilder.getItems()));
    }

    linear(style: LinearLayoutStyle, build: (b: ContentBuilder) => void): void {
        const child = new ContentBuilder();
        build(child);
        this.items.push(new LinearLayoutItem(child.getItems(), style));
    }

    row(style: Omit<LinearLayoutStyle, 'orientation'>, build: (b: ContentBuilder) => void): void {
        this.linear({ ...style, orientation: 'horizontal' }, build);
    }

    col(style: Omit<LinearLayoutStyle, 'orientation'>, build: (b: ContentBuilder) => void): void {
        this.linear({ ...style, orientation: 'vertical' }, build);
    }

    item(item: UiItem): void {
        this.items.push(item);
    }

    grid(cells: UiItem[], style: GridLayoutStyle): void {
        this.items.push(new GridLayoutItem(cells, style));
    }

    wrappedText(
        text: string,
        variant: TextVariant = 'body',
        minLines: number = 0,
        options: WrappedTextOptions = {},
    ): void {
        this.items.push(new WrappedTextItem(text, variant, minLines, options));
    }
}
